/**
 * Client-side PDF page rendering: renders pages as JPEG images using pdf.js,
 * uploads to Supabase storage, and returns public URLs.
 *
 * This approach handles ALL PDF image encodings (DCTDecode/JPEG, FlateDecode,
 * CCITTFaxDecode, inline images, vector diagrams) because it renders the
 * final visual output rather than trying to extract raw image streams.
 */

import pdfjsWorkerUrl from "pdfjs-dist/build/pdf.worker.min.mjs?url";
import { supabase } from "@/integrations/supabase/client";

const MAX_PAGES = 60;
const RENDER_SCALE = 1.5; // ~108 DPI — good balance of quality vs. size
const JPEG_QUALITY = 0.75;

let workerReady = false;

async function ensureWorker() {
  if (workerReady) return;
  const pdfjsLib = await import("pdfjs-dist");
  const gwo = (pdfjsLib as { GlobalWorkerOptions?: { workerSrc?: string }; version?: string }).GlobalWorkerOptions;
  if (typeof window !== "undefined" && gwo && !gwo.workerSrc) {
    gwo.workerSrc = pdfjsWorkerUrl;
    if (!gwo.workerSrc) {
      gwo.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${(pdfjsLib as { version?: string }).version || "4.0.379"}/pdf.worker.min.mjs`;
    }
    workerReady = true;
  }
}

export interface PageRenderResult {
  page: number;
  url: string;
}

/**
 * Render each page of a PDF as a JPEG, upload to lesson-images storage,
 * and return public URLs. Designed for subsequent Gemini figure identification.
 */
export async function renderAndUploadPdfPages(
  pdfStoragePath: string,
  materialId: string,
  fileType: string,
  onProgress?: (current: number, total: number) => void,
): Promise<PageRenderResult[]> {
  if (typeof window === "undefined") return [];

  const isUrl = pdfStoragePath.startsWith("http://") || pdfStoragePath.startsWith("https://");
  let arrayBuffer: ArrayBuffer;

  if (isUrl) {
    const res = await fetch(pdfStoragePath);
    if (!res.ok) throw new Error(`Failed to fetch PDF: ${res.status}`);
    arrayBuffer = await res.arrayBuffer();
  } else {
    const { data, error } = await supabase.storage.from("materials").download(pdfStoragePath);
    if (error || !data) throw new Error(`Failed to download PDF: ${error?.message}`);
    arrayBuffer = await data.arrayBuffer();
  }

  await ensureWorker();
  const pdfjsLib = await import("pdfjs-dist");
  const getDocument = (pdfjsLib as unknown as { getDocument: (o: unknown) => { promise: Promise<unknown> } }).getDocument;
  const pdf = (await getDocument({ data: new Uint8Array(arrayBuffer) }).promise) as {
    numPages: number;
    getPage: (n: number) => Promise<{
      getViewport: (o: { scale: number }) => { width: number; height: number };
      render: (o: { canvasContext: CanvasRenderingContext2D; viewport: unknown }) => { promise: Promise<void> };
    }>;
  };

  const pageCount = Math.min(pdf.numPages, MAX_PAGES);
  const results: PageRenderResult[] = [];

  for (let i = 1; i <= pageCount; i++) {
    onProgress?.(i, pageCount);
    try {
      const page = await pdf.getPage(i);
      const viewport = page.getViewport({ scale: RENDER_SCALE });

      const canvas = document.createElement("canvas");
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      const ctx = canvas.getContext("2d");
      if (!ctx) continue;

      await page.render({ canvasContext: ctx, viewport }).promise;

      const blob: Blob = await new Promise((resolve, reject) => {
        canvas.toBlob(
          (b) => (b ? resolve(b) : reject(new Error("toBlob failed"))),
          "image/jpeg",
          JPEG_QUALITY,
        );
      });

      const path = `pdf-pages/${materialId}/${i}.jpg`;
      const { error: uploadErr } = await supabase.storage
        .from("lesson-images")
        .upload(path, blob, { contentType: "image/jpeg", upsert: true });

      if (uploadErr) {
        console.warn(`Page ${i} upload failed:`, uploadErr.message);
        continue;
      }

      const { data: urlData } = supabase.storage.from("lesson-images").getPublicUrl(path);
      if (urlData?.publicUrl) {
        results.push({ page: i, url: urlData.publicUrl });
      }

      // Release memory
      canvas.width = 0;
      canvas.height = 0;
    } catch (e) {
      console.warn(`Page ${i} render failed:`, e);
    }
  }

  return results;
}
